import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '../api';
import { SearchBarCore } from '../components/SearchBarCore';
import { Shell } from '../components/Shell';
import { RichInlineMd } from '../components/RichInlineMd';
import './BrowsePage.css';
import { useMeProfile } from '../utils/useMeProfile';
import { moduleScoreForProfile } from '../utils/whyPersonalization';
import {
  chipLabel,
  matchesSearch,
  type ExploreModule,
  type ExploreRes,
  moduleHasTopic,
  primaryTopicForModule,
} from './exploreClusters';
import exploreWatercolorBackground from '../assets/explore-watercolor-bg.png';

export function BrowsePage() {
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [chips, setChips] = useState<Set<string>>(() => new Set());
  const [data, setData] = useState<ExploreRes | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { explicit } = useMeProfile();

  const adaptEnabled = (explicit?.adaptRecommendations ?? true) !== false;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiJson<ExploreRes>('/api/explore?limit=400');
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let raf = 0;
    const applyParallax = () => {
      raf = 0;
      const y = window.scrollY || window.pageYOffset || 0;
      const drift = Math.min(42, y * 0.06);
      const driftSoft = Math.min(26, y * 0.035);
      document.documentElement.style.setProperty('--wi-explore-parallax-y', `${drift.toFixed(2)}px`);
      document.documentElement.style.setProperty('--wi-explore-parallax-y-soft', `${driftSoft.toFixed(2)}px`);
    };
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(applyParallax);
    };
    applyParallax();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
      document.documentElement.style.removeProperty('--wi-explore-parallax-y');
      document.documentElement.style.removeProperty('--wi-explore-parallax-y-soft');
    };
  }, []);

  useEffect(() => {
    if (!data) return;
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const nodes = Array.from(document.querySelectorAll<HTMLElement>('.wi-browse-card, .wi-browse-theme'));
    if (!nodes.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting) e.target.classList.add('is-in-view');
      },
      { threshold: 0.2, rootMargin: '-10% 0px -12% 0px' }
    );
    for (const n of nodes) observer.observe(n);
    return () => observer.disconnect();
  }, [data, deferredQuery, chips.size]);

  const toggleChip = (id: string) => {
    setChips((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredModules = useMemo(() => {
    if (!data?.modules) return [];
    const base = data.modules.filter((m) => {
      if (!matchesSearch(m, deferredQuery)) return false;
      if (chips.size === 0) return true;
      for (const chip of chips) {
        if (moduleHasTopic(m, chip)) return true;
      }
      return false;
    });
    if (!adaptEnabled || !explicit) return base;
    const scored = base.map((m) => {
      return { m, s: moduleScoreForProfile(m, 'foundations', explicit) };
    });
    scored.sort((a, b) => b.s - a.s);
    return scored.map((x) => x.m);
  }, [data?.modules, deferredQuery, chips, adaptEnabled, explicit]);

  const hasActiveFilters = query.trim().length > 0 || chips.size > 0;

  const clearFilters = () => {
    setQuery('');
    setChips(new Set());
  };

  return (
    <Shell>
      <div className="wi-browse wi-browse--nizam">
        <header className="wi-browse-hero" aria-labelledby="wi-browse-title">
          <div className="wi-browse-hero__veil" aria-hidden />
          <div className="wi-browse-hero__inner">
            <p className="wi-mk-kicker">Full library</p>
            <h1 id="wi-browse-title" className="wi-browse-hero__title">
              Browse
            </h1>
            <p className="wi-browse-hero__lede">
              Search every topic, filter by domain, and scan the open catalog — or jump into curated Big Ideas when you want a guided sweep.
            </p>
            <div className="wi-browse-hero__links">
              <Link to="/explore" className="wi-browse-hero__link">
                Big Ideas <span aria-hidden>→</span>
              </Link>
              <Link to="/journeys" className="wi-browse-hero__link">
                Journeys <span aria-hidden>→</span>
              </Link>
            </div>
          </div>
        </header>

        <section className="wi-browse-controls" aria-label="Search and filters">
          <div className="wi-browse-controls__inner">
            <div className="wi-browse-controls__top">
              <p className="wi-browse-controls__kicker">Find a topic</p>
              <p className="wi-browse-controls__sub">Search the full library, or use category filters to narrow what you see.</p>
            </div>

            <div className="wi-browse-controls__search">
              <label htmlFor="wi-browse-search" className="visually-hidden">
                Search questions, concepts, or topics
              </label>
              <SearchBarCore
                variant="page"
                inputId="wi-browse-search"
                value={query}
                onValueChange={setQuery}
                placeholder="Search questions, concepts, or topics — not just keywords"
                className="wi-browse-search"
              />
            </div>

            <div className="wi-browse-chips" role="group" aria-label="Topic categories">
              {data?.topics.map((topic) => {
                const active = chips.has(topic.slug);
                return (
                  <button
                    key={topic.slug}
                    type="button"
                    className={`wi-browse-chip${active ? ' wi-browse-chip--active' : ''}`}
                    aria-pressed={active}
                    onClick={() => toggleChip(topic.slug)}
                  >
                    {chipLabel(topic)}
                  </button>
                );
              })}
            </div>

            {data ? (
              <div className="wi-browse-controls__meta">
                <p className="wi-browse-controls__count">
                  {filteredModules.length} result{filteredModules.length === 1 ? '' : 's'}
                  {deferredQuery.trim() ? ' for your search' : ''}
                  {chips.size ? ` · ${chips.size} filter${chips.size === 1 ? '' : 's'} on` : ''}
                  {query !== deferredQuery ? <span className="wi-browse-controls__updating"> Updating…</span> : null}
                </p>
                {hasActiveFilters ? (
                  <button type="button" className="wi-browse-controls__clear" onClick={clearFilters}>
                    Clear
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>

        {error ? <p className="wi-browse-error">{error}</p> : null}
        {!data && !error ? (
          <div className="wi-browse-loading" aria-busy aria-label="Loading browse content">
            <div className="wi-browse-loading__grid">
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={i} className="wi-browse-skeleton" />
              ))}
            </div>
          </div>
        ) : null}

        {data ? (
          <>
            <section className="wi-browse-results" aria-label="All matching topics">
              <div className="wi-browse-results__inner">
                <header className="wi-browse-results__head">
                  <h2 className="wi-browse-results__title">Open exploration</h2>
                  <p className="wi-browse-results__blurb">
                    A dense catalog of everything that matches your search and filters — useful when you want breadth after curated clusters on Big Ideas.
                  </p>
                </header>

                {filteredModules.length ? (
                  <div className="wi-browse-grid">
                    {filteredModules.map((m: ExploreModule) => {
                      const primaryTopic = primaryTopicForModule(m, data.topics);
                      const bg = exploreWatercolorBackground;
                      return (
                        <Link
                          key={m.slug}
                          to={`/m/${m.slug}`}
                          className="wi-browse-card"
                          style={{ ['--wi-card-bg' as any]: `url(${bg})` }}
                        >
                          <div className="wi-browse-card__veil" aria-hidden />
                          <div className="wi-browse-card__top">
                            <span className="wi-browse-card__domain">{primaryTopic ? primaryTopic.title : m.topic_family ?? 'Topic'}</span>
                            <span className="wi-browse-card__meta">{m.estimated_read_minutes ?? 12} min</span>
                          </div>
                          <div className="wi-browse-card__title">{m.title}</div>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <p className="wi-browse-empty">No modules match your search and filters. Try clearing filters or broadening your search.</p>
                )}
              </div>
            </section>

            {data.topics.length ? (
              <section className="wi-browse-themes" aria-label="Theme index">
                <div className="wi-browse-themes__inner">
                  <header className="wi-browse-themes__head">
                    <h2 className="wi-browse-themes__title">Theme index</h2>
                    <p className="wi-browse-themes__intro">Cross-cutting labels used across modules (editorial taxonomy).</p>
                  </header>
                  <div className="wi-browse-themes__grid">
                    {data.topics.map((t) => (
                      <article key={t.id} className="wi-browse-theme">
                        <div className="wi-browse-theme__row">
                          <div className="wi-browse-theme__name">{t.title}</div>
                          {t.family ? <div className="wi-browse-theme__fam">{t.family}</div> : null}
                        </div>
                        {t.summary ? <RichInlineMd text={t.summary} className="wi-browse-theme__sum" /> : null}
                      </article>
                    ))}
                  </div>
                </div>
              </section>
            ) : null}
          </>
        ) : null}
      </div>
    </Shell>
  );
}
